import { get, put, post, del } from '@aws-amplify/api';

export interface NotificationPreferences {
  // Notification channels
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  
  // Notification types
  deadlineReminders: boolean;
  followUpReminders: boolean;
  classificationComplete: boolean;
  externalResponseNeeded: boolean;
  
  // Priority filtering
  minNotificationPriority: 'low' | 'medium' | 'high' | 'urgent';
  
  // Quiet hours
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  quietHoursTimezone: string;
}

export interface NotificationPreferencesResponse {
  success: boolean;
  data?: NotificationPreferences;
  error?: string;
}

/**
 * Get user's notification preferences
 */
export const getNotificationPreferences = async (): Promise<NotificationPreferencesResponse> => {
  try {
    const response = await get({ 
      apiName: 'outlook-assistant-api', 
      path: '/user/notification-preferences' 
    }).response;
    const data = await response.body.json() as unknown as NotificationPreferences;
    
    return {
      success: true,
      data,
    };
  } catch (error: any) {
    console.error('Failed to get notification preferences:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to load notification preferences',
    };
  }
};

/**
 * Update user's notification preferences
 */
export const updateNotificationPreferences = async (
  preferences: NotificationPreferences
): Promise<NotificationPreferencesResponse> => {
  try {
    const response = await put({ 
      apiName: 'outlook-assistant-api', 
      path: '/user/notification-preferences',
      options: { body: JSON.stringify(preferences) }
    }).response;
    const data = await response.body.json() as unknown as NotificationPreferences;
    
    return {
      success: true,
      data,
    };
  } catch (error: any) {
    console.error('Failed to update notification preferences:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to save notification preferences',
    };
  }
};

/**
 * Test notification delivery
 */
export const testNotification = async (
  channel: 'email' | 'push' | 'sms',
  type: 'deadline_reminder' | 'follow_up_reminder' | 'classification_complete' | 'external_response_needed'
): Promise<{ success: boolean; error?: string }> => {
  try {
    await post({ 
      apiName: 'outlook-assistant-api', 
      path: '/user/test-notification',
      options: { body: JSON.stringify({ channel, type }) }
    }).response;
    
    return { success: true };
  } catch (error: any) {
    console.error('Failed to send test notification:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to send test notification',
    };
  }
};

/**
 * Get notification delivery history
 */
export const getNotificationHistory = async (
  limit: number = 50,
  nextToken?: string
): Promise<{
  success: boolean;
  data?: {
    notifications: Array<{
      id: string;
      type: string;
      channel: string;
      status: 'delivered' | 'failed' | 'pending';
      timestamp: string;
      message: string;
    }>;
    nextToken?: string;
  };
  error?: string;
}> => {
  try {
    const queryParams: any = { limit };
    if (nextToken) {
      queryParams.nextToken = nextToken;
    }

    const response = await get({ 
      apiName: 'outlook-assistant-api', 
      path: '/user/notification-history',
      options: { queryParams }
    }).response;
    const data = await response.body.json() as any;
    
    return {
      success: true,
      data,
    };
  } catch (error: any) {
    console.error('Failed to get notification history:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to load notification history',
    };
  }
};

/**
 * Subscribe to web push notifications
 */
export const subscribeToWebPush = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      return {
        success: false,
        error: 'This browser does not support notifications',
      };
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return {
        success: false,
        error: 'Notification permission denied',
      };
    }

    // Register service worker for push notifications
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      const registration = await navigator.serviceWorker.register('/sw.js');
      
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
      });

      // Send subscription to backend
      await post({ 
        apiName: 'outlook-assistant-api', 
        path: '/user/push-subscription',
        options: {
          body: JSON.stringify({
            subscription: subscription.toJSON(),
          })
        }
      }).response;

      return { success: true };
    } else {
      return {
        success: false,
        error: 'Push messaging is not supported',
      };
    }
  } catch (error: any) {
    console.error('Failed to subscribe to web push:', error);
    return {
      success: false,
      error: error.message || 'Failed to enable push notifications',
    };
  }
};

/**
 * Unsubscribe from web push notifications
 */
export const unsubscribeFromWebPush = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          
          // Remove subscription from backend
          await del({ 
            apiName: 'outlook-assistant-api', 
            path: '/user/push-subscription' 
          }).response;
        }
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error('Failed to unsubscribe from web push:', error);
    return {
      success: false,
      error: error.message || 'Failed to disable push notifications',
    };
  }
};

/**
 * Convert frontend preferences to backend format
 */
export const convertToBackendFormat = (frontendPrefs: any): NotificationPreferences => {
  const channels: ('email' | 'push' | 'sms')[] = [];
  if (frontendPrefs.emailNotifications) channels.push('email');
  if (frontendPrefs.pushNotifications) channels.push('push');
  if (frontendPrefs.smsNotifications) channels.push('sms');

  const types: string[] = [];
  if (frontendPrefs.deadlineReminders) types.push('deadline_reminder');
  if (frontendPrefs.followUpReminders) types.push('follow_up_reminder');
  if (frontendPrefs.classificationComplete) types.push('classification_complete');
  if (frontendPrefs.externalResponseNeeded) types.push('external_response_needed');

  return {
    emailNotifications: frontendPrefs.emailNotifications,
    pushNotifications: frontendPrefs.pushNotifications,
    smsNotifications: frontendPrefs.smsNotifications,
    deadlineReminders: frontendPrefs.deadlineReminders,
    followUpReminders: frontendPrefs.followUpReminders,
    classificationComplete: frontendPrefs.classificationComplete,
    externalResponseNeeded: frontendPrefs.externalResponseNeeded,
    minNotificationPriority: frontendPrefs.minNotificationPriority,
    quietHoursEnabled: frontendPrefs.quietHoursEnabled,
    quietHoursStart: frontendPrefs.quietHoursStart,
    quietHoursEnd: frontendPrefs.quietHoursEnd,
    quietHoursTimezone: frontendPrefs.quietHoursTimezone,
  };
};

/**
 * Convert backend preferences to frontend format
 */
export const convertFromBackendFormat = (backendPrefs: NotificationPreferences): any => {
  return {
    emailNotifications: backendPrefs.emailNotifications,
    pushNotifications: backendPrefs.pushNotifications,
    smsNotifications: backendPrefs.smsNotifications,
    deadlineReminders: backendPrefs.deadlineReminders,
    followUpReminders: backendPrefs.followUpReminders,
    classificationComplete: backendPrefs.classificationComplete,
    externalResponseNeeded: backendPrefs.externalResponseNeeded,
    minNotificationPriority: backendPrefs.minNotificationPriority,
    quietHoursEnabled: backendPrefs.quietHoursEnabled,
    quietHoursStart: backendPrefs.quietHoursStart,
    quietHoursEnd: backendPrefs.quietHoursEnd,
    quietHoursTimezone: backendPrefs.quietHoursTimezone,
  };
};